import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import supabase from '../../lib/supabase';
import { useAuth } from '../../hooks/useSupabase';
import { useAuditLog } from '../../hooks/useAuditLog';
import { useAdminRole } from '../../hooks/useAdminRole';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiArrowLeft, FiUserPlus, FiTrash2, FiShield, FiActivity, 
  FiSearch, FiClock, FiCheck, FiAlertCircle, FiKey, FiX, FiLock
} = FiIcons;

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [admins, setAdmins] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // Added password field to state
  const [newAdmin, setNewAdmin] = useState({ email: '', name: '', role: 'editor', password: '' });
  const [passwordReset, setPasswordReset] = useState({ email: '', newPassword: '' });
  
  const [searchLog, setSearchLog] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  
  const navigate = useNavigate();
  const { user } = useAuth();
  const { logAction } = useAuditLog();
  const { canManageUsers, isSuperAdmin } = useAdminRole();

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const { data, error } = await supabase
          .from('authorized_admins_aox1234')
          .select('*')
          .order('created_at', { ascending: false });
        if (error) throw error;
        setAdmins(data || []);
      } else {
        const { data, error } = await supabase
          .from('audit_logs_aox1234')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100);
        if (error) throw error;
        setLogs(data || []);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
      setMessage({ type: 'error', text: 'Σφάλμα φόρτωσης δεδομένων' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    if (!canManageUsers) {
      setMessage({ type: 'error', text: 'Δεν έχετε δικαίωμα προσθήκης χρηστών' });
      return;
    }

    if (newAdmin.password.length < 6) {
      setMessage({ type: 'error', text: 'Ο κωδικός πρέπει να έχει τουλάχιστον 6 χαρακτήρες.' });
      return;
    }

    try {
      // Use the new RPC function to create the user in Auth AND Authorized table
      const { error } = await supabase.rpc('admin_create_user', {
        new_email: newAdmin.email,
        new_password: newAdmin.password,
        new_name: newAdmin.name,
        new_role: newAdmin.role
      });

      if (error) throw error;

      await logAction('create', 'user', `Created user and admin access: ${newAdmin.email}`);
      
      setMessage({ type: 'success', text: 'Ο χρήστης δημιουργήθηκε επιτυχώς και μπορεί να συνδεθεί!' });
      setShowAddForm(false);
      setNewAdmin({ email: '', name: '', role: 'editor', password: '' });
      fetchData();
    } catch (err) {
      console.error('Error adding user:', err);
      setMessage({ type: 'error', text: `Σφάλμα: ${err.message}` });
    }
  };

  const handleDeleteUser = async (id, email) => {
    if (!canManageUsers) return;
    if (!window.confirm('Είστε βέβαιοι; Ο χρήστης δεν θα έχει πλέον πρόσβαση διαχειριστή.')) return;
    
    try {
      const { error } = await supabase
        .from('authorized_admins_aox1234')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await logAction('delete', 'user', `Removed admin access: ${email}`);
      setMessage({ type: 'success', text: 'Ο χρήστης διαγράφηκε.' });
      fetchData();
    } catch (err) {
      setMessage({ type: 'error', text: 'Σφάλμα κατά τη διαγραφή' });
    }
  };

  const openPasswordModal = (email) => {
    setPasswordReset({ email, newPassword: '' });
    setShowPasswordModal(true);
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!isSuperAdmin) return;

    try {
      const { error } = await supabase.rpc('admin_update_user_password', {
        target_email: passwordReset.email,
        new_password: passwordReset.newPassword
      });

      if (error) throw error;

      await logAction('update', 'user_password', `Reset password for: ${passwordReset.email}`);
      setMessage({ type: 'success', text: 'Ο κωδικός ενημερώθηκε επιτυχώς!' });
      setShowPasswordModal(false);
    } catch (err) {
      console.error('Password reset error:', err);
      setMessage({ type: 'error', text: `Σφάλμα: ${err.message || 'Αποτυχία ενημέρωσης'}` });
    }
  };

  const filterLogs = logs.filter(log => 
    log.user_email.toLowerCase().includes(searchLog.toLowerCase()) ||
    log.description.toLowerCase().includes(searchLog.toLowerCase()) ||
    log.resource.toLowerCase().includes(searchLog.toLowerCase())
  );

  const formatDate = (date) => new Date(date).toLocaleString('el-GR');

  if (!canManageUsers && activeTab === 'users') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white p-8 rounded-lg shadow-md text-center max-w-md">
          <SafeIcon icon={FiLock} className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Περιορισμένη Πρόσβαση</h2>
          <p className="text-gray-600 mb-6">Μόνο ο Superadmin μπορεί να διαχειριστεί χρήστες.</p>
          <button onClick={() => navigate('/admin/dashboard')} className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Επιστροφή
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <button onClick={() => navigate('/admin/dashboard')} className="p-2 text-gray-500 hover:bg-gray-100 rounded-md">
              <SafeIcon icon={FiArrowLeft} className="w-5 h-5" />
            </button>
            <h1 className="text-lg font-semibold text-gray-900">Διαχείριση Χρηστών & Ιστορικό</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="flex space-x-4 mb-6 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('users')}
            className={`pb-3 px-4 font-medium flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'users' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <SafeIcon icon={FiShield} className="w-4 h-4" />
            <span>Χρήστες</span>
          </button>
          <button
            onClick={() => setActiveTab('logs')}
            className={`pb-3 px-4 font-medium flex items-center space-x-2 border-b-2 transition-colors ${
              activeTab === 'logs' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <SafeIcon icon={FiActivity} className="w-4 h-4" />
            <span>Ιστορικό Ενεργειών</span>
          </button>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-md flex items-center ${message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
            <SafeIcon icon={message.type === 'success' ? FiCheck : FiAlertCircle} className="w-5 h-5 mr-2" />
            {message.text}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && isSuperAdmin && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="flex justify-end mb-4">
              <button 
                onClick={() => setShowAddForm(!showAddForm)}
                className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                <SafeIcon icon={FiUserPlus} className="w-4 h-4 mr-2" />
                Προσθήκη Χρήστη
              </button>
            </div>

            {showAddForm && (
              <div className="bg-white p-6 rounded-lg shadow-md mb-6">
                <h3 className="text-lg font-semibold mb-4">Νέος Χρήστης</h3>
                <form onSubmit={handleAddUser} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                      <input
                        type="email"
                        placeholder="user@example.com"
                        required
                        value={newAdmin.email}
                        onChange={e => setNewAdmin({...newAdmin, email: e.target.value})}
                        className="w-full border p-2 rounded focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Κωδικός *</label>
                      <input
                        type="text"
                        placeholder="Τουλάχιστον 6 χαρακτήρες"
                        required
                        minLength={6}
                        value={newAdmin.password}
                        onChange={e => setNewAdmin({...newAdmin, password: e.target.value})}
                        className="w-full border p-2 rounded focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Όνομα</label>
                      <input
                        type="text"
                        placeholder="Ονοματεπώνυμο"
                        value={newAdmin.name}
                        onChange={e => setNewAdmin({...newAdmin, name: e.target.value})}
                        className="w-full border p-2 rounded focus:ring-2 focus:ring-primary-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Ρόλος</label>
                      <select
                        value={newAdmin.role}
                        onChange={e => setNewAdmin({...newAdmin, role: e.target.value})}
                        className="w-full border p-2 rounded focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="editor">Editor (No Delete)</option>
                        <option value="admin">Admin (Full Content)</option>
                        <option value="superadmin">Superadmin</option>
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                      Δημιουργία Χρήστη
                    </button>
                  </div>
                </form>
              </div>
            )}

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Όνομα</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ρόλος</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Ενέργειες</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {admins.map(admin => (
                    <tr key={admin.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{admin.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.name || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          admin.role === 'superadmin' ? 'bg-purple-100 text-purple-800' :
                          admin.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {admin.role.toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button 
                            onClick={() => openPasswordModal(admin.email)} 
                            className="text-blue-600 hover:text-blue-900 p-1 bg-blue-50 rounded"
                            title="Αλλαγή Κωδικού"
                          >
                            <SafeIcon icon={FiKey} className="w-4 h-4" />
                          </button>
                          
                          {admin.email !== user.email && (
                            <button 
                              onClick={() => handleDeleteUser(admin.id, admin.email)} 
                              className="text-red-600 hover:text-red-900 p-1 bg-red-50 rounded"
                              title="Διαγραφή Χρήστη"
                            >
                              <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="mb-4 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SafeIcon icon={FiSearch} className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Αναζήτηση στο ιστορικό..."
                value={searchLog}
                onChange={(e) => setSearchLog(e.target.value)}
                className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm p-2 border"
              />
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-gray-500">Φόρτωση...</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ημερομηνία</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Χρήστης</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ενέργεια</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Περιγραφή</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filterLogs.map(log => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 flex items-center">
                            <SafeIcon icon={FiClock} className="w-3 h-3 mr-1" />
                            {formatDate(log.created_at)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.user_email}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              log.action === 'create' ? 'bg-green-100 text-green-800' :
                              log.action === 'delete' ? 'bg-red-100 text-red-800' :
                              log.action === 'update' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100'
                            }`}>
                              {log.action.toUpperCase()} {log.resource}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{log.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </div>

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold">Αλλαγή Κωδικού</h3>
              <button onClick={() => setShowPasswordModal(false)} className="text-gray-400 hover:text-gray-600">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              Ορισμός νέου κωδικού για <strong>{passwordReset.email}</strong>
            </p>
            <form onSubmit={handlePasswordReset}>
              <input
                type="password"
                placeholder="Νέος κωδικός"
                className="w-full border p-2 rounded mb-4 focus:ring-2 focus:ring-primary-500"
                value={passwordReset.newPassword}
                onChange={(e) => setPasswordReset({...passwordReset, newPassword: e.target.value})}
                required
                minLength={6}
              />
              <button 
                type="submit" 
                className="w-full bg-primary-600 text-white py-2 rounded hover:bg-primary-700"
              >
                Ενημέρωση
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;